import moment from "moment-timezone";
import type { WaitlistEntry } from "../../../../types/waitlist";

/** A toast's two lines (desktop notify / mobile MobileToast). */
export interface WaitlistToast {
  title: string;
  note?: string;
}

/** "Maria Lopez". */
export const entryClientName = (
  e: Pick<WaitlistEntry, "client_first_name" | "client_last_name">,
): string =>
  `${e.client_first_name ?? ""} ${e.client_last_name ?? ""}`.trim() || "Client";

/** "45 min" / "1 hr" / "1 hr 15 min" (prototype `dur`). */
export const durationText = (mins: number): string => {
  const m = Math.max(0, Math.round(mins || 0));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return `${h} hr${r ? ` ${r} min` : ""}`;
};

/**
 * Elapsed wait (prototype `waitText`): "just now", "1 min ago",
 * "18 mins ago", "1 hr ago", "1.5 hrs ago" (hours to one decimal).
 */
export const waitText = (minutes: number): string => {
  const min = Math.max(0, Math.floor(minutes));
  if (min < 1) return "just now";
  if (min < 60) return `${min} ${min === 1 ? "min" : "mins"} ago`;
  const h = Math.round((min / 60) * 10) / 10;
  const text = h % 1 ? h.toFixed(1) : String(h);
  return `${text} ${h === 1 ? "hr" : "hrs"} ago`;
};

/** Minutes between arrival (ISO UTC) and `nowMs`. */
export const minutesWaited = (arrivedAt: string, nowMs: number): number => {
  const t = moment.utc(arrivedAt).valueOf();
  return Number.isFinite(t) ? Math.max(0, (nowMs - t) / 60000) : 0;
};

/** Arrival clock in the salon's timezone: "1:42 PM". */
export const arrivedClock = (arrivedAt: string, timezone: string): string =>
  moment.utc(arrivedAt).tz(timezone).format("h:mm A");

/** Ids with `id` moved by `delta` (−1 up, +1 down); null at an edge. */
export const moveIdBy = (
  ids: readonly string[],
  id: string,
  delta: number,
): string[] | null => {
  const i = ids.indexOf(id);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= ids.length) return null;
  const next = [...ids];
  next.splice(i, 1);
  next.splice(j, 0, id);
  return next;
};

/** Entries in the order of `ids` (unknown ids dropped, missing rows appended). */
export const orderEntries = (
  entries: readonly WaitlistEntry[],
  ids: readonly string[],
): WaitlistEntry[] => {
  const byId = new Map(entries.map((e) => [e.uuid, e]));
  const ordered = ids
    .map((id) => byId.get(id))
    .filter((e): e is WaitlistEntry => !!e);
  const seen = new Set(ordered.map((e) => e.uuid));
  return [...ordered, ...entries.filter((e) => !seen.has(e.uuid))];
};

/** Insert or replace by uuid (a sync refetch may have landed it first). */
export const upsertEntry = (
  entries: readonly WaitlistEntry[],
  entry: WaitlistEntry,
): WaitlistEntry[] =>
  entries.some((e) => e.uuid === entry.uuid)
    ? entries.map((e) => (e.uuid === entry.uuid ? entry : e))
    : [...entries, entry];

/** Grip label for screen readers (prototype). */
export const gripLabel = (name: string, index: number, total: number) =>
  `Reorder ${name} (position ${index + 1} of ${total}). Use arrow keys to move.`;

// ── Toast copy (prototype) ───────────────────────────────────────────

export const addedToast = (
  client: string,
  service: string,
  position: number,
): WaitlistToast => ({
  title: `Added ${client} to the waitlist`,
  note: `${service} · #${position} in line`,
});

export const updatedToast = (
  client: string,
  service: string,
): WaitlistToast => ({
  title: "Walk-in updated",
  note: `${client} · ${service}`,
});

export const removedToast = (
  client: string,
  service: string,
): WaitlistToast => ({
  title: `Removed ${client} from the waitlist`,
  note: service,
});
